import styled from 'styled-components';

import { Logo as LogoElement } from '~/elements/Logo';

export const Logo = styled(LogoElement)`
  margin: auto;
`;

const DESKTOP_LOGO_TARGET = { x: 24, y: 18, scale: 16 / 24 };
const MOBILE_LOGO_TARGET = { x: 12, y: 19, scale: 14 / 24 };
const FINAL_LETTER_OFFSET = { x: 31, y: 7 };

export const animateLogo = (
  logo: SVGElement,
  logoWrapper: HTMLDivElement,
  panel: HTMLDivElement,
  gs: any,
  pathPlugin: any,
  callback: () => void
) => {
  gs.registerPlugin(pathPlugin);

  const element = logo.getBoundingClientRect();

  const sidebarPlaceholders = document.getElementsByClassName('sidebar-placeholder');
  const sidebarPlaceholder = sidebarPlaceholders.length > 0 ? sidebarPlaceholders[0] : null;

  // Try to find the actual sidebar logo to get accurate target coordinates
  const sidebarLogo = document.querySelector('[data-logo-target]') as SVGElement;
  const fallbackTarget = window.matchMedia('(min-width: 1025px)').matches ? DESKTOP_LOGO_TARGET : MOBILE_LOGO_TARGET;
  let targetX = fallbackTarget.x;
  let targetY = fallbackTarget.y;
  let targetScale = fallbackTarget.scale;

  if (sidebarLogo) {
    const targetRect = sidebarLogo.getBoundingClientRect();
    targetX = targetRect.left;
    targetY = targetRect.top;
    targetScale = targetRect.height / element.height;
  }

  // The animated SVG includes the mark to the left of the wordmark. The mark fades out, so offset the SVG itself
  // to align the remaining letters with the wordmark rendered in the app shell.
  const logoTargetX = targetX - FINAL_LETTER_OFFSET.x * targetScale;
  const logoTargetY = targetY - FINAL_LETTER_OFFSET.y * targetScale;

  if (element) {
    const panelRect = panel.getBoundingClientRect();
    const frozenPanel = panel.cloneNode(true) as HTMLDivElement;
    const frozenLogo = logo.cloneNode(true) as SVGElement;
    const letters = frozenLogo.querySelectorAll(':scope > path');
    const shape = frozenLogo.querySelector('g path');

    Object.assign(frozenPanel.style, {
      position: 'fixed',
      left: `${panelRect.left}px`,
      top: `${panelRect.top}px`,
      width: `${panelRect.width}px`,
      height: `${panelRect.height}px`,
      margin: '0',
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: '19',
    });
    Object.assign(frozenLogo.style, {
      position: 'fixed',
      left: `${element.left}px`,
      top: `${element.top}px`,
      width: `${element.width}px`,
      height: `${element.height}px`,
      margin: '0',
      transform: 'none',
      transformOrigin: '0 0',
      willChange: 'transform',
      zIndex: '20',
    });
    document.body.appendChild(frozenPanel);
    document.body.appendChild(frozenLogo);
    panel.style.visibility = 'hidden';
    logo.style.visibility = 'hidden';

    gs.killTweensOf([logo, logoWrapper, panel, frozenLogo, frozenPanel, shape, ...letters]);

    const timeline = gs.timeline({
      onComplete: () => {
        frozenPanel.remove();
        callback();
        gs.to(frozenLogo, {
          opacity: 0,
          duration: 0.4,
          ease: 'none',
          onComplete: () => frozenLogo.remove(),
        });
      },
    });
    timeline.to(
      frozenPanel,
      {
        '--panel-o': 0,
        '--panel-y': '0px',
        '--panel-s': 0.9,
        duration: 0.35,
        ease: 'power2.out',
      },
      0
    );
    timeline.to(shape, { opacity: 0, duration: 0.45, ease: 'power2.out' }, 0);
    timeline.to(
      frozenLogo,
      {
        motionPath: {
          path: [
            {
              x: (logoTargetX - element.left) / 1.9,
              y: (logoTargetY - element.top) / 1.4,
            },
            {
              x: logoTargetX - element.left,
              y: logoTargetY - element.top,
            },
          ],
          curviness: 0.75,
        },
        duration: 1,
        ease: 'power2.inOut',
      },
      0
    );
    timeline.to(frozenLogo, { rotate: '5deg', repeat: 1, yoyo: true, duration: 0.5 }, 0);
    timeline.to(frozenLogo, { scale: targetScale, duration: 0.9, ease: 'power2.inOut' }, 0);
    timeline.to(
      letters,
      {
        stagger: -0.08,
        '--logo-path-opacity': 1,
        '--logo-path-x': '-1px',
        '--logo-path-y': '7px',
        duration: 1,
      },
      0
    );

    if (sidebarPlaceholder) {
      timeline.to(sidebarPlaceholder, { x: '100%', opacity: 1, duration: 0.8 }, 0.2);
    }
  }
};
